import type { CSSProperties, ReactNode } from 'react'

import { MarketingPageShell } from '@/components/landing/marketing-page-shell'
import { MarketingSiteFooter } from '@/components/landing/marketing-site-footer'
import { CURRENT_CLIENT } from '@/constants'
import { getPluginSlots } from '@/plugin/slots'
import { getMarketingShellChrome } from '@/landing/marketing-shell-chrome'
import type { ClientWithLanding } from '@/landing/resolve-landing'
import { MarketingContentFrame } from '@/landing/marketing-content-frame'
import { cn } from '@/lib/utils'

/** Bottom rhythm under share content. Overlay chrome still needs top clearance. */
const PUBLIC_SHARE_SHELL_Y = 'pb-6 sm:pb-8 lg:pb-10'

/** TalentCo public share reading column — nav and banner stay on the 1200px marketing measure. */
export const TALENTCO_PUBLIC_SHARE_BODY_MAX_WIDTH_PX = 1000

type PublicShareLayoutProps = {
  children: ReactNode
  className?: string
  /** Banner / identity overlap — stays on the full share column, not the narrower body. */
  hero?: ReactNode
}

function PublicShareBody({ children }: { children: ReactNode }) {
  const maxWidth = getPluginSlots().publicShareBodyMaxWidthPx
  if (!maxWidth) return children
  return (
    <div className="mx-auto w-full" style={{ maxWidth }}>
      {children}
    </div>
  )
}

/** Marketing chrome + centered column body for shareable public artifact pages (no agent shell). */
export function PublicShareLayout({ children, className, hero }: PublicShareLayoutProps) {
  const chrome = getMarketingShellChrome(CURRENT_CLIENT as ClientWithLanding)
  const shareMax = getPluginSlots().publicShareBodyMaxWidthPx
  const talentCoShareStyle =
    shareMax != null
      ? ({
          '--public-share-body-max-width': `${shareMax}px`,
        } as CSSProperties)
      : undefined

  return (
    <MarketingPageShell
      transparentShell={chrome.transparentShell}
      stickyOverlayHeader={chrome.stickyOverlayHeader}
      stickyHeaderSolid={chrome.stickyHeaderSolid}
      stickyHeaderContentInset={chrome.stickyHeaderContentInset}
      signInLabel={chrome.signInLabel}
      registerLabel={chrome.registerLabel}
      hideRegister={chrome.hideRegister}
      hideSignIn={chrome.hideSignIn}
      footer={<MarketingSiteFooter />}
    >
      <MarketingContentFrame
        shellClassName={cn(
          PUBLIC_SHARE_SHELL_Y,
          chrome.stickyOverlayHeader && 'mt-8'
        )}
        className={className}
        style={talentCoShareStyle}
      >
        {hero ? (
          <div className="space-y-8">
            {hero}
            <PublicShareBody>{children}</PublicShareBody>
          </div>
        ) : (
          <PublicShareBody>{children}</PublicShareBody>
        )}
      </MarketingContentFrame>
    </MarketingPageShell>
  )
}
